// src/components/profile/ChangePassword.tsx
import React, { useState } from 'react';
import api from '../../api/client';
import { useNotification } from '../../context/NotificationContext'; // Assuming this path is correct

const ChangePassword: React.FC = () => {
  const { notify } = useNotification(); // Use the notification hook

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // State for basic client-side validation feedback (optional, can be integrated with notification)
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);

  const validateNewPassword = (password: string) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter.';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter.';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number.';
    }
    // You can add more rules, e.g., special characters
    return null; // No error
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    const validationError = validateNewPassword(value);
    setNewPasswordError(validationError);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (newPassword !== confirmPassword) {
      notify('error', 'New passwords do not match.');
      setLoading(false);
      return;
    }

    const validationError = validateNewPassword(newPassword);
    if (validationError) {
      notify('error', validationError);
      setLoading(false);
      return;
    }

    try {
      await api.put('/auth/change-password/', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      notify('success', 'Password changed successfully!');
      // Clear form fields on success
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setNewPasswordError(null); // Clear any lingering validation errors
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.response?.data?.new_password?.[0] || 'Failed to change password. Please check your current password.';
      notify('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">Change Your Password</h1>
      <div className="max-w-xl mx-auto bg-white p-8 rounded-lg shadow-xl border border-gray-200">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="oldPassword" className="block text-sm font-semibold text-gray-700 mb-1">Current Password</label>
            <input
              type="password"
              id="oldPassword"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out"
              required
            />
          </div>
          <div>
            <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={handleNewPasswordChange} // Use the new handler for validation
              className={`mt-1 block w-full px-4 py-2 border ${newPasswordError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out`}
              required
            />
            {newPasswordError && (
              <p className="mt-1 text-sm text-red-600">{newPasswordError}</p>
            )}
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading || !!newPasswordError} // Disable if loading or new password has errors
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;